import moment from 'moment';
import { SubSink } from 'subsink';

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { FormControl, FormControlState, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { ApmTranslatePipe, AuthFacade, DomainDataFacade, UserPermission } from '@mitsubishi/core';
import { ProjectSelectionsFacade } from '@mitsubishi/project/data-access';
import { CustomUnit, Prices } from '@mitsubishi/shared/data-access';
import { ButtonComponent, CurrencyInputComponent, SectionTitleComponent, SelectModule } from '@mitsubishi/shared/ui';
import { UiDatepickerModule } from '@mitsubishi/ui/datepicker';

export type CustomUnitDialogComponentData = {
  uta?: boolean;
  alternativeId?: string;
  unit?: CustomUnit;
  alternativeUnitPrices?: Prices;
  readonly?: boolean;
};
@Component({
  selector: 'apm-custom-unit-dialog',
  templateUrl: './custom-unit-dialog.component.html',
  styleUrls: ['./custom-unit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    UiDatepickerModule,
    CurrencyInputComponent,
    SectionTitleComponent,
    ApmTranslatePipe,
    ButtonComponent,
    SelectModule,
  ],
})
export class CustomUnitDialogComponent implements OnInit {
  #domainDataFacade = inject(DomainDataFacade);
  #authFacade = inject(AuthFacade);
  #selectionFacade = inject(ProjectSelectionsFacade);

  dialogRef = inject(MatDialogRef<CustomUnitDialogComponent>);
  dialogData = inject(MAT_DIALOG_DATA) as CustomUnitDialogComponentData;

  hasCustomAndRfqManagePermission = this.#authFacade.hasPermission(UserPermission.ManageCustomAndRfq);
  customUnitForm = new UntypedFormGroup({});

  currencySymbol = this.#selectionFacade.currencySymbol;
  segments$ = this.#domainDataFacade.segments$;
  brands$ = this.#domainDataFacade.brands$;
  productionSites = this.#domainDataFacade.productionSites;

  subs: SubSink = new SubSink();

  get isUta() {
    return this.dialogData.uta ?? false;
  }

  get unit() {
    return this.dialogData.unit;
  }

  get unitPrices() {
    return this.dialogData.alternativeUnitPrices;
  }

  get initialValidityDate() {
    const date = new Date();
    date.setDate(date.getDate() + 60);
    return date;
  }

  constructor() {
    this.dialogRef.disableClose = true;
  }

  ngOnInit() {
    const defaultValidators = this.hasCustomAndRfqManagePermission ? [Validators.required] : [];
    const validityDate = this.unit?.validityDate ? moment(this.unit.validityDate) : this.initialValidityDate;
    const priceListPrice = this.dialogData.readonly ? this.unitPrices?.customer : this.unitPrices?.priceList;

    this.customUnitForm = new UntypedFormGroup({
      name: new FormControl(this.unit?.name, [Validators.required]),
      segmentId: new FormControl(this.unit?.segmentId, [Validators.required]),
      priceList: new FormControl(priceListPrice, this.isUta ? [] : defaultValidators),
      validityDate: new FormControl(
        this.isUta ? validityDate : this.getFormValueBasedOnPermission(validityDate),
        this.isUta ? [] : [Validators.required]
      ),
      productionSiteCode: new FormControl(
        this.getFormValueBasedOnPermission(this.unit?.productionSiteCode || 'EUROPE')
      ),
    });
    if (this.dialogData.uta) {
      this.customUnitForm.addControl('commodityCode', new FormControl('UTA'));
      this.customUnitForm.addControl('materialCode', new FormControl('UTA'));
    } else {
      this.customUnitForm.addControl('brandId', new FormControl(this.unit?.brandId, [Validators.required]));
      this.customUnitForm.addControl(
        'sapOfferNumber',
        new FormControl(this.getFormValueBasedOnPermission(this.unit?.sapOfferNumber))
      );
      this.customUnitForm.addControl(
        'atpCode',
        new FormControl(this.getFormValueBasedOnPermission(this.dialogData.unit?.atpCode))
      );
      this.customUnitForm.addControl(
        'commodityCode',
        new FormControl(this.getFormValueBasedOnPermission(this.unit?.commodityCode), defaultValidators)
      );
      this.customUnitForm.addControl(
        'materialCode',
        new FormControl(this.getFormValueBasedOnPermission(this.unit?.materialCode), defaultValidators)
      );
      this.customUnitForm.addControl(
        'positionCode',
        new FormControl(
          this.getFormValueBasedOnPermission(this.dialogData.unit?.positionCode),
          this.hasCustomAndRfqManagePermission ? [Validators.pattern('^[0-9]*$')] : []
        )
      );

      this.subs.sink = this.customUnitForm.controls['sapOfferNumber'].valueChanges.subscribe(value => {
        if (value) {
          this.customUnitForm.controls['positionCode'].addValidators(defaultValidators);
        } else {
          this.customUnitForm.controls['positionCode'].removeValidators(defaultValidators);
        }
        this.customUnitForm.controls['positionCode'].updateValueAndValidity();
      });
    }

    if (this.dialogData.readonly) {
      this.customUnitForm.disable();
    }
  }

  create() {
    if (this.dialogData.unit) {
      this.dialogRef.close(this.customUnitForm.getRawValue());
    } else {
      this.dialogRef.close({
        alternativeId: this.dialogData.alternativeId,
        ...this.dialogData,
        ...this.customUnitForm.getRawValue(),
      });
    }
  }

  closeDialog() {
    this.dialogRef.close();
    this.subs.unsubscribe();
  }

  private getFormValueBasedOnPermission(value: unknown): FormControlState<unknown> {
    return { value, disabled: !this.hasCustomAndRfqManagePermission };
  }
}
